/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable no-invalid-this */

'use strict';

// MODULES //

import IS_LITTLE_ENDIAN from '@stdlib/assert-is-little-endian';
import { isPrimitive as isNumber } from '@stdlib/assert-is-number';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import { isPrimitive as isInteger } from '@stdlib/assert-is-integer';
import { isPrimitive as isBigInt } from '@stdlib/assert-is-bigint';
import isComplexLike from '@stdlib/assert-is-complex-like';
import isAllowedCast from '@stdlib/ndarray-base-assert-is-allowed-data-type-cast';
import minDataType from '@stdlib/array-min-dtype';
import complexDType from '@stdlib/complex-dtype';
import BigInt from '@stdlib/bigint-ctor';
import Number from '@stdlib/number-ctor';
import floor from '@stdlib/math-base-special-floor';
import format from '@stdlib/error-tools-fmtprodmsg';
import PRIVATE_BUFFER from './private_buffer.js';
import boolean2bigint from './boolean2bigint.js';
import defaults from './defaults.json';


// MAIN //

/**
* Returns a function for writing field data.
*
* @private
* @param {Object} obj - field object
* @param {string} method - data view method name
* @returns {Function} function for writing field data
*/
function setBigInt( obj, method ) {
	return setter;

	/**
	* Writes a number value to an underlying byte buffer.
	*
	* @private
	* @param {*} value - value to set
	* @throws {TypeError} cannot cast provided values to field data type
	*/
	function setter( value ) {
		var view;
		var dt;
		var v;
		if ( isBigInt( value ) ) {
			dt = 'int64'; // FIXME: support both int64 and uint64
			v = value;
		} else if ( isNumber( value ) ) {
			if ( isInteger( value ) ) {
				dt = minDataType( value );
				v = BigInt( value );
			} else {
				dt = defaults.dtypes.real;
				v = BigInt( floor( value ) );
			}
		} else if ( isBoolean( value ) ) {
			dt = 'bool';
			v = boolean2bigint( value );
		} else if ( isComplexLike( value ) ) {
			dt = complexDType( value ) || defaults.dtypes.complex;
			v = BigInt( floor( value.re ) ); // discard imaginary component
		} else {
			dt = 'generic';
			v = BigInt( floor( Number( v ) ) );
		}
		if ( !isAllowedCast( dt, obj.type, obj.castingMode ) ) {
			throw new TypeError( format( '2KJIY', obj.name, obj.type, dt ) );
		}
		view = this[ PRIVATE_BUFFER ];
		view[ method ]( obj.byteOffset, v, IS_LITTLE_ENDIAN );
	}
}


// EXPORTS //

export default setBigInt;
